import { makeAutoObservable, computed } from 'mobx';
import { DEFAULT_VIEW_FILTERS, Icon, RawView, ViewFilters, VIEWS_FILTER_TYPE } from '../interfaces/view';
import { createObservableView, ViewModel } from '../models/view';
import { ICON_TYPE, STATUS_TYPE_LABELS, TASK_PRIORITY_LABELS } from '../../global/constants';
import { rootStore } from '../rootStore';
import { filterTasks } from '../../services/mobxTaskFilterService';
import { nanoid } from 'nanoid';
import { MobxFilteredTasksListInterface, NewTaskFilterInterface } from '../../interfaces/TaskInterface';
import { defaultDisplayFilters } from '../common';
import { getWorkspaceAllViewsPreferenceData } from '../../helpers/firebaseHelper';
import { captureException } from '@sentry/react';

export class CreateViewInput {
  id: string = '';
  name?: string;
  description?: string;
  icon: Icon = {
    type: ICON_TYPE.DEFAULT,
    color: 1,
    reference: ''
  };
  color?: number = 1;
  userIds?: string[] = [];
  filters: ViewFilters = DEFAULT_VIEW_FILTERS;

  displayFilters: NewTaskFilterInterface = defaultDisplayFilters;

  constructor() {
    makeAutoObservable(this, {
      generatedDescription: computed
    });
  }

  get generatedDescription() {
    let description = 'Tasks ';
    let hasPreviousFilter = false;

    if (this.filters.status && this.filters.status.statuses.length > 0) {
      if (this.filters.status.type === VIEWS_FILTER_TYPE.IS) {
        if (this.filters.status.statuses.length === 1) {
          const statusLabel = STATUS_TYPE_LABELS[this.filters.status.statuses[0]];
          description += `that are ${statusLabel}`;
        } else {
          const statusLabels = this.filters.status.statuses.slice(0, -1).map((status) => STATUS_TYPE_LABELS[status]);
          const lastStatus = this.filters.status.statuses[this.filters.status.statuses.length - 1];
          const lastStatusLabel = STATUS_TYPE_LABELS[lastStatus];
          description += `that are either ${statusLabels.join(', ')} or ${lastStatusLabel}`;
        }
      } else {
        if (this.filters.status.statuses.length === 1) {
          const statusLabel = STATUS_TYPE_LABELS[this.filters.status.statuses[0]];
          description += `that are not ${statusLabel}`;
        } else {
          const statusLabels = this.filters.status.statuses.slice(0, -1).map((status) => STATUS_TYPE_LABELS[status]);
          const lastStatus = this.filters.status.statuses[this.filters.status.statuses.length - 1];
          const lastStatusLabel = STATUS_TYPE_LABELS[lastStatus];
          description += `that are not ${statusLabels.join(', ')} or ${lastStatusLabel}`;
        }
      }
      hasPreviousFilter = true;
    }

    if (this.filters.priority && this.filters.priority.priorities.length > 0) {
      if (this.filters.priority.type === VIEWS_FILTER_TYPE.IS) {
        if (this.filters.priority.priorities.length === 1) {
          const priorityLabel = TASK_PRIORITY_LABELS[this.filters.priority.priorities[0]];
          description += `${hasPreviousFilter ? ' and' : 'that'} have ${priorityLabel} priority`;
        } else {
          const priorityLabels = this.filters.priority.priorities
            .slice(0, -1)
            .map((priority) => TASK_PRIORITY_LABELS[priority]);
          const lastPriority = this.filters.priority.priorities[this.filters.priority.priorities.length - 1];
          const lastPriorityLabel = TASK_PRIORITY_LABELS[lastPriority];
          description += `${hasPreviousFilter ? ' and' : 'that'} have either ${priorityLabels.join(
            ', '
          )} or ${lastPriorityLabel} priority`;
        }
      } else {
        if (this.filters.priority.priorities.length === 1) {
          const priorityLabel = TASK_PRIORITY_LABELS[this.filters.priority.priorities[0]];
          description += `${hasPreviousFilter ? ' and' : 'that'} do not have ${priorityLabel} priority`;
        } else {
          const priorityLabels = this.filters.priority.priorities
            .slice(0, -1)
            .map((priority) => TASK_PRIORITY_LABELS[priority]);
          const lastPriority = this.filters.priority.priorities[this.filters.priority.priorities.length - 1];
          const lastPriorityLabel = TASK_PRIORITY_LABELS[lastPriority];
          description += `${hasPreviousFilter ? ' and' : 'that'} do not have ${priorityLabels.join(
            ', '
          )} or ${lastPriorityLabel} priority`;
        }
      }
      hasPreviousFilter = true;
    }

    if (this.filters.assign && this.filters.assign.assignees.length > 0) {
      if (this.filters.assign.assignees.length === 1) {
        const assigneeId = this.filters.assign.assignees[0];
        const assignee = rootStore.workspaceStore.currentWorkspace?.users.find((user) => user.id === assigneeId);
        description += `${hasPreviousFilter ? ' and' : 'that'} are${
          this.filters.assign.type === VIEWS_FILTER_TYPE.IS_NOT ? ' not' : ''
        } assigned to ${assignee?.name}`;
      } else if (this.filters.assign.assignees.length <= 3) {
        const assigneeIds = this.filters.assign.assignees.slice(0, -1);
        const lastAssigneeId = this.filters.assign.assignees[this.filters.assign.assignees.length - 1];
        const assigneeLabels = assigneeIds.map((assigneeId) => {
          const assignee = rootStore.workspaceStore.currentWorkspace?.users.find((user) => user.id === assigneeId);
          return assignee?.name;
        });
        const lastAssignee = rootStore.workspaceStore.currentWorkspace?.users.find(
          (user) => user.id === lastAssigneeId
        );
        description += `${hasPreviousFilter ? ' and' : 'that'} are${
          this.filters.assign.type === VIEWS_FILTER_TYPE.IS_NOT ? ' not' : ''
        } assigned to either ${assigneeLabels.join(', ')} or ${lastAssignee?.name}`;
      } else {
        description += `${hasPreviousFilter ? ' and' : 'that'} are${
          this.filters.assign.type === VIEWS_FILTER_TYPE.IS_NOT ? ' not' : ''
        } assigned to multiple users`;
      }
      hasPreviousFilter = true;
    }

    if (this.filters.dueDate && this.filters.dueDate.date.length > 0) {
      const dueDate = this.filters.dueDate.date[0];
      if (dueDate === 'No due date') {
        description += `${hasPreviousFilter ? ' and' : 'that'} ${
          this.filters.dueDate.type === VIEWS_FILTER_TYPE.IS_NOT ? 'do not' : ''
        } have a due date`;
      } else if (dueDate === 'In the past') {
        description += `${hasPreviousFilter ? ' and' : 'that'} are${
          this.filters.dueDate.type === VIEWS_FILTER_TYPE.IS_NOT ? ' not' : ''
        } overdue`;
      } else {
        const dateLabel = dueDate.toLowerCase();
        if (this.filters.dueDate.type === VIEWS_FILTER_TYPE.AFTER) {
          description += `${hasPreviousFilter ? ' and' : 'that'} are due ${dateLabel}`;
        } else if (this.filters.dueDate.type === VIEWS_FILTER_TYPE.BEFORE) {
          description += `${hasPreviousFilter ? ' and' : 'that'} are due before ${dateLabel}`;
        }
      }
      hasPreviousFilter = true;
    }

    if (this.filters.project && this.filters.project.projects.length > 0) {
      if (this.filters.project.projects.length === 1) {
        const projectId = this.filters.project.projects[0];
        const project = rootStore.projectStore.projects.find((p) => p.id === projectId);
        description += `${hasPreviousFilter ? ' and' : 'that'} ${
          this.filters.project.type === VIEWS_FILTER_TYPE.IS_NOT ? 'do not' : ''
        } belong to ${project?.name}`;
      } else if (this.filters.project.projects.length <= 3) {
        const projectIds = this.filters.project.projects.slice(0, -1);
        const lastProjectId = this.filters.project.projects[this.filters.project.projects.length - 1];
        const projectLabels = projectIds.map((projectId) => {
          const project = rootStore.projectStore.projects.find((p) => p.id === projectId);
          return project?.name;
        });
        const lastProject = rootStore.projectStore.projects.find((p) => p.id === lastProjectId);
        description += `${hasPreviousFilter ? ' and' : 'that'} ${
          this.filters.project.type === VIEWS_FILTER_TYPE.IS_NOT ? 'do not' : ''
        } belong to either ${projectLabels.join(', ')} or ${lastProject?.name}`;
      } else {
        description += `${hasPreviousFilter ? ' and' : 'that'}${
          this.filters.project.type === VIEWS_FILTER_TYPE.IS_NOT ? ' do not' : ''
        } belong to multiple projects`;
      }
      hasPreviousFilter = true;
    }

    return hasPreviousFilter ? description + '.' : '';
  }

  updateWith(view: ViewModel) {
    this.id = view?.id || nanoid();
    this.description = view?.description;
    this.name = view?.name;
    this.filters = view?.filters || DEFAULT_VIEW_FILTERS;
    this.icon = view?.icon || { type: ICON_TYPE.DEFAULT, image: '' };
    this.color = view?.color || 1;
    this.userIds = view?.userIds;
    this.displayFilters = view?.displayFilters || defaultDisplayFilters;
  }

  clear() {
    this.id = '';
    this.description = '';
    this.name = '';
    this.filters = DEFAULT_VIEW_FILTERS;
    this.icon = { type: ICON_TYPE.DEFAULT, color: 1, reference: '' };
    this.color = 1;
  }

  setId(id: string) {
    this.id = id;
  }

  setName(name: string) {
    this.name = name;
  }

  setDescription(description: string) {
    this.description = description;
  }

  setIcon(type: number, reference: string) {
    // this.icon = icon;
    this.icon = { ...this.icon, type, reference };
  }

  setColor(color: number) {
    this.color = color;
    this.icon = { ...this.icon, color };
  }

  setFilter(filters: ViewFilters) {
    this.filters = filters;
  }

  setStatusFilter(statuses: number[], type: number) {
    this.filters.status = {
      statuses,
      type
    };
  }

  setPriorityFilter(priorities: number[], type: number) {
    this.filters.priority = {
      priorities,
      type
    };
  }

  setAssignFilter(assignees: string[], type: number) {
    this.filters.assign = {
      assignees,
      type
    };
  }

  setDueDateFilter(date: string[], type: number) {
    this.filters.dueDate = { date, type };

    if (type === VIEWS_FILTER_TYPE.RANGE && date.length === 2) {
      this.filters.dueDate.startDate = date[0];
      this.filters.dueDate.endDate = date[1];
    }
  }

  setProjectFilter(projects: string[], type: number) {
    this.filters.project = {
      projects,
      type
    };
  }

  setCustomerFilter(customers: string[], type: number) {
    this.filters.customer = {
      customers,
      type
    };
  }

  updateTaskFilterType<K extends keyof NewTaskFilterInterface>(propsName: K, value: NewTaskFilterInterface[K]) {
    this.displayFilters[propsName] = value;
  }
}

export class ViewStore {
  loading: boolean = false;
  views: ViewModel[] = [];
  currentView: ViewModel | null = null;
  createViewInput: CreateViewInput = new CreateViewInput();

  constructor() {
    makeAutoObservable(this);
  }

  get viewTasks() {
    // Add filtering logic based on current filter
    const tasks =
      (this.currentView && this.currentView?.displayFilters?.showSubTask) ||
      this.createViewInput?.displayFilters?.showSubTask
        ? rootStore.taskStore.tasks
        : rootStore.taskStore.tasks?.filter((task) => !task.parentTaskId);

    const filters = this.createViewInput?.id ? this.createViewInput?.filters : this.currentView?.filters;

    const displayFilters = this.createViewInput?.id
      ? this.createViewInput?.displayFilters
      : this.currentView?.displayFilters;

    if (!this.currentView && !this.createViewInput?.id) {
      return {
        filteredTasks: [] as MobxFilteredTasksListInterface[],
        otherTasks: { tasks: [], completedTasks: [] },
        hiddenTasks: 0
      };
    }
    return filterTasks(tasks, filters!, displayFilters!);
  }

  setViews(rawView: RawView[]) {
    this.views = rawView.map((rawView) => createObservableView(rawView));
  }

  setCurrentView(viewId: string) {
    this.currentView = this.views.find((view) => view.id === viewId) || null;
  }

  setLoading(loading: boolean) {
    this.loading = loading;
  }

  async updateViewPreferenceFromFirebase() {
    try {
      // One-time bulk read of all view preferences, then distribute to each view. This
      // replaces the previous per-view getDoc() loop (N reads for N views) with a single
      // collection read.
      const allViewsPreferenceData = await getWorkspaceAllViewsPreferenceData();
      for (const view of this.views) {
        const viewPreference = allViewsPreferenceData?.[view.id] as Partial<NewTaskFilterInterface> | undefined;
        if (viewPreference) {
          view.updateViewPreferenceFromFirebase(viewPreference);
        }
      }
    } catch (err) {
      captureException(err);
    }
  }
}

export const viewStore = new ViewStore();
